import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ConsultationSheet, ConsultationType } from './ConsultationSheet';
import { FileTextIcon, StethoscopeIcon, PillIcon, BrainIcon, ScaleIcon, BookOpenIcon, LayersIcon, PackageIcon, ChevronRightIcon } from 'lucide-react';
const smallCards = [{
  title: 'Free Doctor\nConsultation',
  icon: <StethoscopeIcon className="w-5 h-5 md:w-7 md:h-7 text-teal-500" data-id="element-2378" data-mp="ntmvvkb1m19y" />,
  link: '/doctor-consultation',
  badge: {
    text: 'FREE',
    color: 'bg-[#FF9F43]'
  },
  iconBg: 'bg-teal-50'
}, {
  title: 'Ask a\nPharmacist',
  icon: <PillIcon className="w-5 h-5 md:w-7 md:h-7 text-orange-500" data-id="element-2379" data-mp="a3jfh9yuh1sf" />,
  link: '/pharmacist-consultation',
  badge: {
    text: 'FREE',
    color: 'bg-[#FF9F43]'
  },
  iconBg: 'bg-orange-50'
}, {
  title: 'AI Nutrition\n& Diet',
  icon: <BrainIcon className="w-5 h-5 md:w-7 md:h-7 text-purple-500" data-id="element-2380" data-mp="12rfama8rmln3" />,
  link: '/nutrition-calculator',
  badge: {
    text: 'New',
    color: 'bg-[#FF4757]'
  },
  iconBg: 'bg-purple-50'
}, {
  title: 'BMI\nCalculator',
  icon: <ScaleIcon className="w-5 h-5 md:w-7 md:h-7 text-green-500" data-id="element-2381" data-mp="qv9vnpcgjnsn" />,
  link: '/bmi-calculator',
  badge: {
    text: 'New',
    color: 'bg-[#FF4757]'
  },
  iconBg: 'bg-green-50'
}, {
  title: 'Ingredient\nGlossary',
  icon: <BookOpenIcon className="w-5 h-5 md:w-7 md:h-7 text-blue-500" data-id="element-2382" data-mp="32ta34tihdol" />,
  link: '/ingredient-glossary',
  iconBg: 'bg-blue-50'
}, {
  title: 'Stacking\nGuide',
  icon: <LayersIcon className="w-5 h-5 md:w-7 md:h-7 text-amber-500" data-id="element-2383" data-mp="3q8u039vvgxu7" />,
  link: '/stacking-guide',
  iconBg: 'bg-amber-50'
}, {
  title: 'Bundle\nBuilder',
  icon: <PackageIcon className="w-5 h-5 md:w-7 md:h-7 text-pink-500" data-id="element-2384" data-mp="3uc8ig77a9qat" />,
  link: '/bundle-builder',
  iconBg: 'bg-pink-50'
}];
export function FeatureCardsSection() {
  const navigate = useNavigate();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [activeConsultation, setActiveConsultation] = useState<ConsultationType | null>(null);
  const didDragRef = useRef(false);
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!sliderRef.current) return;
    didDragRef.current = false;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
  };
  const handleMouseLeave = () => {
    setIsDragging(false);
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    sliderRef.current.scrollLeft = scrollLeft - walk;
    if (Math.abs(walk) > 5) {
      didDragRef.current = true;
    }
  };
  const handleCardClick = (link: string) => {
    if (didDragRef.current) return;
    if (link === '/doctor-consultation') {
      setActiveConsultation('doctor');
      return;
    }
    if (link === '/pharmacist-consultation') {
      setActiveConsultation('pharmacist');
      return;
    }
    navigate(link);
  };
  return <>
      <ConsultationSheet type={activeConsultation} onClose={() => setActiveConsultation(null)} data-id="element-2385" data-mp="o6x039yuv3i6" />
      <div className="container mx-auto px-4" data-id="element-2386" data-mp="y778mh9y3mr0">
        {/* Section Header */}
        <div className="mb-4" data-id="element-2387" data-mp="y54epm5o99h0">
          <div className="flex items-center gap-2 mb-1" data-id="element-2388" data-mp="12xdyekg71dau">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900" data-id="element-2389" data-mp="1300h9g8jbf8d">
              Pharmacy Services
            </h2>
            <span className="bg-[#1E2B55] text-white text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-2390" data-mp="1bylx33mmgg5g">
              24/7
            </span>
          </div>
          <p className="text-sm md:text-base text-gray-600" data-id="element-2391" data-mp="1gygn74ykp1pg">
            From your home, order your prescription or consult a doctor or
            pharmacist!
          </p>
        </div>

        {/* Services Container */}
        <div className="bg-[#F4FBF7] rounded-2xl md:rounded-3xl p-3 md:p-6 border border-green-50" data-id="element-2392" data-mp="z7a5uiqig950">
          <div className="flex flex-col gap-3 md:gap-4" data-id="element-2393" data-mp="1d2vq6clsu4r3">
            {/* Top Row: Prescription */}
            <button onClick={() => navigate('/prescription')} className="bg-white rounded-xl md:rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow text-left w-full group" data-id="element-2394" data-mp="183rijqeiwf2v">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0 relative overflow-hidden" data-id="element-2395" data-mp="uat5mq61e4bk">
                <div className="absolute inset-0 bg-green-100 opacity-50 transform -skew-x-12" data-id="element-2396" data-mp="p9vsirzvw0gx"></div>
                <motion.div animate={{
                scale: [1, 1.1, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }} className="relative z-10" data-id="element-2397" data-mp="25rjhopx55qd3">
                  <FileTextIcon className="w-6 h-6 md:w-8 md:h-8 text-green-500" data-id="element-2398" data-mp="39ntwqos2emsp" />
                </motion.div>
              </div>
              <div className="flex-1 min-w-0" data-id="element-2399" data-mp="3en6kdbqrfxwc">
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-0.5 md:mb-1" data-id="element-2400" data-mp="66k3cdo2ct5l">
                  Order via Prescription
                </h3>
                <p className="text-xs md:text-sm text-gray-500 line-clamp-2 md:line-clamp-none" data-id="element-2401" data-mp="a6vqpy7t97kr">
                  Easily order your medications, upload your doctor's
                  prescription.
                </p>
              </div>
              <div className="flex items-center gap-1 text-green-500 font-bold text-sm md:text-base flex-shrink-0 group-hover:translate-x-1 transition-transform" data-id="element-2402" data-mp="1dip87vokxn2">
                Upload <ChevronRightIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-2403" data-mp="txh5wztyye46" />
              </div>
            </button>

            {/* Bottom Row: Remaining Services */}
            <div className="relative group/slider" data-id="element-2404" data-mp="yss912rkg7ln">
              {/* Navigation Arrows - Tablet Only */}
              <button onClick={() => {
              const el = document.getElementById('pharmacy-services-slider');
              if (el) el.scrollBy({
                left: -200,
                behavior: 'smooth'
              });
            }} className="hidden md:flex lg:hidden absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg items-center justify-center text-gray-400 hover:text-green-500 transition-colors opacity-0 group-hover/slider:opacity-100" aria-label="Previous" data-id="element-2405" data-mp="tsxix1fm7m1n">
                <ChevronRightIcon className="w-6 h-6 rotate-180" data-id="element-2406" data-mp="14i4ag0eb5qu" />
              </button>
              <button onClick={() => {
              const el = document.getElementById('pharmacy-services-slider');
              if (el) el.scrollBy({
                left: 200,
                behavior: 'smooth'
              });
            }} className="hidden md:flex lg:hidden absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg items-center justify-center text-gray-400 hover:text-green-500 transition-colors opacity-0 group-hover/slider:opacity-100" aria-label="Next" data-id="element-2407" data-mp="3t9m0i8uh3260">
                <ChevronRightIcon className="w-6 h-6" data-id="element-2408" data-mp="1tzstjw9vmdae" />
              </button>

              <div id="pharmacy-services-slider" ref={sliderRef} onMouseDown={handleMouseDown} onMouseLeave={handleMouseLeave} onMouseUp={handleMouseUp} onMouseMove={handleMouseMove} className={`flex flex-nowrap overflow-x-auto ${isDragging ? 'cursor-grabbing select-none snap-none' : 'cursor-grab snap-x snap-mandatory'} scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 lg:grid lg:grid-cols-7 gap-3 md:gap-4 pb-4 md:pb-0`} data-id="element-2409" data-mp="1yzxf40pn5y7h">
                {smallCards.map((card, idx) => <button key={idx} onClick={() => handleCardClick(card.link)} aria-haspopup={card.link === '/doctor-consultation' || card.link === '/pharmacist-consultation' ? 'dialog' : undefined} className="flex-shrink-0 w-[130px] sm:w-[140px] md:w-auto snap-start bg-white rounded-xl md:rounded-2xl p-3 md:p-4 flex flex-col items-center text-center hover:shadow-md transition-all hover:-translate-y-1 relative pt-7 md:pt-8 border border-transparent hover:border-green-100" data-id="element-2410" data-mp="dqb91vmippw8" data-mpi={idx}>
                    {card.badge && <motion.span animate={{
                  scale: [1, 1.05, 1],
                  boxShadow: ['0 0 0 0px rgba(0, 0, 0, 0)', '0 0 0 4px rgba(0, 0, 0, 0.05)', '0 0 0 0px rgba(0, 0, 0, 0)']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className={`absolute top-2 right-2 ${card.badge.color} text-white text-[10px] md:text-xs font-black px-2.5 py-0.5 rounded-full shadow-sm uppercase tracking-wider`} data-id="element-2411" data-mp="16bcjd39ijmwy">
                        {card.badge.text}
                      </motion.span>}
                    <motion.div whileHover={{
                  scale: 1.1,
                  rotate: 5
                }} className={`w-12 h-12 md:w-16 md:h-16 ${card.iconBg} rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-inner relative group-hover:shadow-md transition-all select-none pointer-events-none`} data-id="element-2412" data-mp="x8pc0bvwu447">
                      <div className="absolute inset-0 bg-white/40 rounded-full scale-0 group-hover:scale-100 transition-transform duration-300" data-id="element-2413" data-mp="2ao8b2c54zkqj"></div>
                      {card.icon}
                    </motion.div>
                    <h3 className="text-xs md:text-sm font-bold text-gray-900 leading-tight whitespace-pre-line" data-id="element-2414" data-mp="2oi9c5cjk9ju2">
                      {card.title}
                    </h3>
                  </button>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
}